/**
 * MccLogo — programme wordmark.
 *
 * Variants:
 *  - mark: just the symbol (square) — three depth-band glyph
 *  - inline: pure-text two-tone wordmark (bold "MCC" + thin separator + stacked
 *            "Marine / Conservation Credits"). This is the canonical brand mark
 *            used across the site header, footers and embedded email templates.
 *  - stacked: legacy symbol + small wordmark (kept for backwards compatibility,
 *             not used in current chrome).
 *
 * Symbol (mark variant): three layered horizontal bands evoking ocean depth
 * strata (surface → mesopelagic → benthic), with a registry serial-tick on
 * the rightmost band. Built from primitives so it scales crisply at any size
 * and inherits currentColor for monochrome contexts.
 */
import * as React from 'react';

export type MccLogoVariant = 'mark' | 'inline' | 'stacked';

interface MccLogoProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'color'> {
  variant?: MccLogoVariant;
  /** Symbol height in pixels (mark) or wordmark "MCC" font-size (inline/stacked). Width adapts. */
  size?: number;
  /** Override symbol color (mark + stacked). Defaults to brand navy var(--mcc-navy). */
  symbolColor?: string;
  /** Override main "MCC" wordmark color. Defaults to brand navy. */
  textColor?: string;
  /** Override tagline color (inline variant). Defaults to slate gray. */
  taglineColor?: string;
  /** Render in dark-mode contexts (footers etc): white "MCC" + light gray tagline. */
  dark?: boolean;
}

const BRAND_NAVY = 'var(--mcc-navy)';
const TAGLINE_GRAY = 'var(--mcc-muted)';
const SEPARATOR_GRAY = 'var(--mcc-border)';

export function MccLogo({
  variant = 'inline',
  size = 32,
  symbolColor,
  textColor,
  taglineColor,
  dark = false,
  style,
  ...rest
}: MccLogoProps) {
  if (variant === 'mark') {
    // Square symbol — return SVG (must accept SVG props for compatibility with
    // existing call-sites that may pass aria-label, etc.).
    return (
      <svg
        viewBox="0 0 32 32"
        width={size}
        height={size}
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        aria-label="Marine Conservation Credits"
        role="img"
        style={style as React.CSSProperties}
      >
        <Symbol color={symbolColor || BRAND_NAVY} />
      </svg>
    );
  }

  if (variant === 'stacked') {
    const w = size * 4.4;
    return (
      <svg
        viewBox="0 0 140 56"
        width={w}
        height={size * 1.75}
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        aria-label="Marine Conservation Credits"
        role="img"
        style={style as React.CSSProperties}
      >
        <g transform="translate(54, 0)">
          <Symbol color={symbolColor || BRAND_NAVY} />
        </g>
        <text
          x="70"
          y="50"
          textAnchor="middle"
          fontFamily="'Inter', -apple-system, BlinkMacSystemFont, sans-serif"
          fontSize="11"
          fontWeight="600"
          letterSpacing="2.4"
          fill={textColor || BRAND_NAVY}
        >
          MARINE CONSERVATION CREDITS
        </text>
      </svg>
    );
  }

  // Inline (default) — pure text, brandmark-style two-tone wordmark.
  // Large bold "MCC" in navy, thin separator, stacked tagline in gray.
  const mccColor = textColor || (dark ? '#ffffff' : BRAND_NAVY);
  const tagColor = taglineColor || (dark ? 'rgba(255,255,255,0.85)' : TAGLINE_GRAY);
  const sepColor = dark ? 'rgba(255,255,255,0.45)' : SEPARATOR_GRAY;
  return (
    <div
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 12,
        fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
        ...style,
      }}
      aria-label="Marine Conservation Credits"
      role="img"
      {...rest}
    >
      <span
        style={{
          fontSize: size,
          fontWeight: 700,
          color: mccColor,
          letterSpacing: '-0.02em',
          lineHeight: 1,
        }}
      >
        MCC
      </span>
      <div style={{ width: 1, height: size * 0.7, background: sepColor }} />
      <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.1 }}>
        <span style={{ fontSize: Math.max(13, size * 0.5), color: tagColor, fontWeight: 500 }}>Marine</span>
        <span style={{ fontSize: Math.max(13, size * 0.5), color: tagColor, fontWeight: 500 }}>Conservation Credits</span>
      </div>
    </div>
  );
}

function Symbol({ color }: { color?: string }) {
  // 32x32 symbol — three depth strata + serial tick
  const c = color || 'currentColor';
  return (
    <g>
      {/* Outer frame */}
      <rect x="1" y="1" width="30" height="30" rx="6" stroke={c} strokeWidth="1.5" />
      {/* Surface band */}
      <path d="M5 9c2-1 4-1 6 0s4 1 6 0 4-1 6 0 2 0.5 4 0" stroke={c} strokeWidth="1.5" strokeLinecap="round" />
      {/* Mesopelagic band */}
      <path d="M5 16c2-1 4-1 6 0s4 1 6 0 4-1 6 0 2 0.5 4 0" stroke={c} strokeWidth="1.5" strokeLinecap="round" opacity="0.6" />
      {/* Benthic band */}
      <path d="M5 23c2-1 4-1 6 0s4 1 6 0 4-1 6 0 2 0.5 4 0" stroke={c} strokeWidth="1.5" strokeLinecap="round" opacity="0.35" />
      {/* Serial / registry tick — anchor point on right edge */}
      <circle cx="24" cy="9" r="1.4" fill={c} />
    </g>
  );
}
